import DebugService from 'app/shared/debug.service';
import { PanelTabTour } from 'app/shared/help_and_information/tours/TourService';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';

const log = (...args) => DebugService.log('HelpRegistry', ...args);

const searchTagPrefixesForNewItems = ['create', 'creating', 'make', 'making', 'new', 'add'];

export function genericSearchTagsForNewImsItems(name: string): Array<string> {
    return searchTagPrefixesForNewItems.map((str) => `${str} ${name}`);
}

export interface HelpDetails {
    key: string;
    header: string;
    tourType: string;
    /**
     * A list of strings used to categorise this tour
     */
    tags?: Array<string>;
    /**
     * A list of tags used to help in the fuzzy search, but no visible to the user
     */
    hiddenTags?: Array<string>;
    steps?: Array<any>;
    description?: string;
    // Optional. Text will be used for inner html. Will override anything in the description field
    descriptionHtml?: string;
    quickStartGuideSearchText?: string;
    tabbedPanelTour?: Array<PanelTabTour>;
    innerTourSteps?: Array<StepDefinition>;
    onStart?: () => void; // Run on start of tour
    onEnd?: () => void; // Run on end of tour
    onClose?: () => void; // Run when tour bubble X is clicked. Normally this is the same as onEnd
    navigateTo?: string[]; // Navigate to this part of the app, e.g. ['OverviewPane', 'DashboardTab']
    // Run a function before the tour starts. Return a millisecond delay.
    // e.g. waiting for panel to open
    beforeStart?: () => number;
}

export class HelpRegistry {
    private _helpData: Map<string, HelpDetails>;
    private _event: CachedEventRegistry;

    constructor() {
        this._helpData = new Map();
        this._event = new CachedEventRegistry();
    }

    deregister(key: string): void {
        if (this._helpData.has(key)) {
            this._helpData.delete(key);
        }
        this._event.notifyListeners(Array.from(this._helpData.values()));
    }

    register(helpDetails: HelpDetails): void {
        log('registering help', helpDetails);
        if (!helpDetails.key) {
            throw new Error('must provide a key for each tour');
        }
        if (this._helpData.has(helpDetails.key)) {
            throw new Error(`Key '${helpDetails.key}' already exists in this help registry`);
        }
        this._helpData.set(helpDetails.key, helpDetails);
        this._event.notifyListeners(Array.from(this._helpData.values()));
    }

    onRegistryChange(fn: (helpDetailsList: Array<HelpDetails>) => void): () => void {
        return this._event.addEventListener(fn);
    }

    getHelpDetails(helpKey: string): HelpDetails {
        return Object.assign({}, this._helpData.get(helpKey));
    }

    getAllHelpDetails(): Array<HelpDetails> {
        return [].concat(...this._helpData.values());
    }

    helpBtnTooltip(helpKey: string): string {
        return `Open ${this._helpData.get(helpKey).header} Help`;
    }

    hasTour(helpKey: string): boolean {
        return !!this._helpData.get(helpKey)?.tourType || false;
    }
}
